"use client";

import Link from "next/link";
import Image from "next/image";
import { useFooterConfigQuery } from "@/hooks/useFooterConfig";
import type { FooterColumn } from "@/hooks/useFooterConfig";
import { DEFAULT_FOOTER_COLUMNS, DEFAULT_FOOTER_COPYRIGHT, DEFAULT_FOOTER_BRAND } from "@/lib/footer-defaults";

const DEFAULT_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.vendorcall.pro";
const DEFAULT_APP_STORE_URL = "https://apps.apple.com/us/app/vendorcall-pro/id1610322067";

export function SiteFooter() {
  const { data, isLoading } = useFooterConfigQuery();
  const columns = (data?.columns?.length ? data.columns : DEFAULT_FOOTER_COLUMNS) as FooterColumn[];
  const copyrightText = data?.copyrightText ?? DEFAULT_FOOTER_COPYRIGHT;
  const brandLabel = data?.brandLabel ?? DEFAULT_FOOTER_BRAND;
  const playStoreUrl = (data?.playStoreUrl ?? "").trim();
  const appStoreUrl = (data?.appStoreUrl ?? "").trim();
  const resolvedPlayStoreUrl = playStoreUrl || DEFAULT_PLAY_STORE_URL;
  const resolvedAppStoreUrl = appStoreUrl || DEFAULT_APP_STORE_URL;

  if (isLoading) {
    return (
      <footer className="w-full bg-gradient-to-b from-zinc-900 to-zinc-950 border-t border-zinc-800 py-20 text-white min-h-[280px] flex flex-col justify-center">
        <div className="w-full px-12 sm:px-16 lg:px-24">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded bg-zinc-800/50 animate-pulse" />
            ))}
          </div>
          <div className="border-t border-zinc-800 pt-8 mt-8 flex justify-between items-center">
            <div className="h-6 w-32 rounded bg-zinc-800/50 animate-pulse" />
            <div className="h-4 w-48 rounded bg-zinc-800/50 animate-pulse" />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="w-full bg-gradient-to-b from-zinc-900 to-zinc-950 border-t border-zinc-800 py-20 text-white min-h-[280px] flex flex-col justify-center">
      <div className="w-full px-12 sm:px-16 lg:px-24">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8 items-start">
          {columns.map((col, i) => (
            <div key={i}>
              <h3 className="font-semibold text-white mb-4">{col.title}</h3>
              <ul className="space-y-2 text-sm text-zinc-400">
                {col.links?.map((link, j) => (
                  <li key={j}>
                    <Link href={link.href || "#"} className="hover:text-white transition">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="relative w-full md:w-56 shrink-0 -mt-5" style={{ maxWidth: 280 }}>
            <Image
              src="/images/download-app.png"
              alt="Download on the App Store and Google Play"
              width={280}
              height={160}
              className="w-full h-auto object-contain block"
            />
            <a
              href={resolvedPlayStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-0 left-0 right-0 h-1/2 z-10"
              aria-label="Get it on Google Play"
            />
            <a
              href={resolvedAppStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-0 left-0 right-0 h-1/2 z-10"
              aria-label="Download on the App Store"
            />
          </div>
        </div>
        <div className="border-t border-zinc-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link href="/" className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-primary-foreground">VC</span>
              </div>
              <span className="font-semibold text-white">{brandLabel}</span>
            </Link>
            <p className="text-sm text-zinc-500">{copyrightText}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
